import React from "react";
import { gql, useApolloClient } from "@apollo/client";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { Dialog } from "@syos-music/common";

import { Box, Tabs, Tab, Collapse, Button, Backdrop } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import LegacyMouthpieceField from "components/mouthpiece/LegacyMouthpieceField";
import {
  saxophone as saxophoneMouthpieceUtils,
  clarinet as clarinetMouthpieceUtils,
} from "components/mouthpiece/utils";

import SoundShapingDesignField from "./SoundShapingDesignField";
import OrderSidebar from "../../index";
import { MaterialColorSelectField } from "@syos-music/instruments";

const TABS = {
  mouthpieceSearch: require("./tabs/MouthpieceSearch"),
  solidouSaxophone: require("./tabs/SolidouSaxophone"),
  solidouClarinet: require("./tabs/SolidouClarinet"),
  legacyMouthpiece: require("./tabs/LegacyMouthpiece"),
};

export default function CreateSoundShapingDialog({
  order,
  cartItem,
  children,
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const open = () => setIsOpen(true);
  const close = () => {
    setIsOpen(false);
    setIsDesignFieldOpen(false);
    setActiveTab(Object.keys(TABS).shift());
  };

  const [activeTab, setActiveTab] = React.useState(Object.keys(TABS).shift());
  const ActiveTabComponent = TABS[activeTab].default;

  const [isDesignFieldOpen, setIsDesignFieldOpen] = React.useState(false);
  const openDesignField = () => setIsDesignFieldOpen(true);
  const closeDesignField = () => setIsDesignFieldOpen(false);

  const apolloClient = useApolloClient();
  const handleSubmit = React.useCallback(
    async (values) => {
      try {
        const mouthpieceRef = await TABS[activeTab].handleSubmit(
          values,
          apolloClient
        );

        const designValues = {};
        switch (values.design?.designType) {
          case "originals":
            Object.assign(designValues, {
              designType: values.design.designType,
              designOriginals: values.design.designOriginals,
            });
            break;

          case "catalog":
            Object.assign(designValues, {
              designType: values.design.designType,
              designCatalog: values.design.designCatalog,
            });
            break;

          case "text":
            Object.assign(designValues, {
              designType: values.design.designType,
              designText: values.design.designText,
              designTextFont: values.design.designTextFont,
            });
            break;

          case "signature":
            Object.assign(designValues, {
              designType: values.design.designType,
              designSignature: values.design.designSignature,
            });
            break;

          default:
            break;
        }

        await apolloClient.mutate({
          // Avoid fragments circular dependency, build mutation at runtime
          mutation: gql`
            mutation (
              $orderId: ID!
              $cartItemId: ID
              $mouthpieceRef: MouthpieceRefInput!
              $color: String
              $designType: String
              $designCatalog: String
              $designOriginals: String
              $designSignature: ID
              $designText: String
              $designTextFont: String
            ) {
              CreateSoundShaping(
                orderId: $orderId
                cartItemId: $cartItemId
                mouthpieceRef: $mouthpieceRef
                color: $color
                designType: $designType
                designCatalog: $designCatalog
                designOriginals: $designOriginals
                designSignature: $designSignature
                designText: $designText
                designTextFont: $designTextFont
              ) {
                order {
                  id

                  ...OrderSidebar_order
                }
              }
            }

            ${OrderSidebar.fragments.order}
          `,
          variables: {
            orderId: order.id,
            cartItemId: cartItem?.id,
            mouthpieceRef,
            color: values.color,
            ...designValues,
          },
        });

        close();
      } catch (err) {
        return true;
      }
    },
    [activeTab, cartItem?.id, apolloClient, order.id]
  );

  const instrument = {
    customSaxophoneMouthpiece: "saxophone",
    signatureSaxophoneMouthpiece: "saxophone",
    customClarinetMouthpiece: "clarinet",
    signatureClarinetMouthpiece: "clarinet",

    originalsMouthpiece: cartItem.product.defaultOptions?.instrument,
    signatureMouthpiece: cartItem.product.defaultOptions?.instrument,
    customMouthpiece: cartItem.product.defaultOptions?.instrument,
  }[cartItem.product.displayMode];

  if (!instrument) return null;

  const instrumentType = {
    customSaxophoneMouthpiece: cartItem.options?.saxophoneType,
    signatureSaxophoneMouthpiece: cartItem.options?.saxophoneType,
    customClarinetMouthpiece: cartItem?.options?.clarinetType,
    signatureClarinetMouthpiece: cartItem?.options?.clarinetType,

    originalsMouthpiece: cartItem.options?.instrumentType,
    signatureMouthpiece: cartItem.options?.instrumentType,
    customMouthpiece: cartItem.options?.instrumentType,
  }[cartItem.product.displayMode];

  if (!instrumentType) return null;

  let initialDesign;
  switch (cartItem.product.displayMode) {
    case "originalsMouthpiece":
      if (cartItem.options?.originalsType) {
        initialDesign = {
          designType: "originals",
          designOriginals: cartItem.options.originalsType,
        };
      }
      break;

    case "signatureMouthpiece":
    case "signatureSaxophoneMouthpiece":
    case "signatureClarinetMouthpiece":
      if (cartItem.options?.signatureMouthpieceId) {
        initialDesign = {
          designType: "signature",
          designSignature: cartItem.options.signatureMouthpieceId,
        };
      }
      break;

    case "customMouthpiece":
      if (cartItem.options?.designType === "custom") break;
      initialDesign = {
        designType: cartItem.options?.designType,
        designCatalog: cartItem.options.designCatalog,
        designText: cartItem.options?.designText,
        designTextFont: cartItem.options?.designTextFont,
      };
      break;

    case "customSaxophoneMouthpiece":
    case "customClarinetMouthpiece":
      if (cartItem.options?.useDesign) {
        const designType =
          cartItem.options?.designType === "syos"
            ? "catalog"
            : cartItem.options?.designType;

        initialDesign = {
          designType,
          designCatalog: cartItem.options.mouthpieceDesign,
          designText: cartItem.options?.designText,
          designTextFont: cartItem.options?.designTextFont,
        };
      }
      break;

    default:
      break;
  }

  const initialValues = {
    instrument,
    instrumentType,

    solidouSaxophone: saxophoneMouthpieceUtils.applyMouthpieceDefaults({
      saxophoneType: instrumentType,
    }),

    solidouClarinet: clarinetMouthpieceUtils.applyMouthpieceDefaults({
      clarinetType: instrumentType,
    }),

    legacyMouthpiece: {
      instrument,
      saxophoneType: instrumentType,
      clarinetType: instrumentType,
      ...LegacyMouthpieceField.defaultValues,
    },

    color: cartItem.options?.mouthpieceColor,
    design: initialDesign,
  };

  return (
    <>
      {children({ isOpen, open, close })}

      <Dialog open={isOpen} onClose={close} maxWidth="md" disableRestoreFocus>
        <Form
          onSubmit={handleSubmit}
          initialValues={initialValues}
          mutators={{ ...arrayMutators }}
          keepDirtyOnReinitialize
        >
          {({ handleSubmit, submitting, values, form }) => (
            <form onSubmit={handleSubmit} noValidate autoComplete="off">
              <Dialog.Title color="primary">Sound shaping</Dialog.Title>

              <Tabs
                value={activeTab}
                onChange={(evt, tabName) => setActiveTab(tabName)}
                variant="fullWidth"
                sx={{ flexShrink: 0 }}
              >
                {Object.entries(TABS)
                  .filter(([key, mod]) => mod.isAvailable(values))
                  .map(([key, mod]) => (
                    <Tab key={key} value={key} label={<mod.TabLabel />} />
                  ))}
              </Tabs>

              <Dialog.Content
                sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
              >
                <ActiveTabComponent
                  instrument={instrument}
                  instrumentType={instrumentType}
                />
              </Dialog.Content>

              <Backdrop open={isDesignFieldOpen} sx={{ zIndex: 10 }} />

              <Collapse
                in={isDesignFieldOpen}
                sx={{
                  position: "relative",
                  zIndex: 20,
                  backgroundColor: (t) => t.palette.background.paper,
                  flexShrink: 0,
                }}
              >
                <Dialog.Content>
                  <SoundShapingDesignField
                    name="design"
                    instrument={instrument}
                    instrumentType={instrumentType}
                  />
                </Dialog.Content>

                <Dialog.Actions>
                  <Button onClick={closeDesignField}>Valider</Button>
                </Dialog.Actions>
              </Collapse>

              <Dialog.Actions>
                <Box sx={{ minWidth: 240 }}>
                  <MaterialColorSelectField
                    name="color"
                    allowPrivate
                    label=""
                    placeholder="Couleur"
                  />
                </Box>

                <Button variant="text" onClick={openDesignField}>
                  <SoundShapingDesignField.Summary
                    fieldName="design"
                    instrument={instrument}
                  />
                </Button>

                <LoadingButton type="submit" loading={submitting}>
                  Enregistrer
                </LoadingButton>
              </Dialog.Actions>
            </form>
          )}
        </Form>
      </Dialog>
    </>
  );
}

CreateSoundShapingDialog.fragments = {
  order: gql`
    fragment OrderSidebar_SoundShaping_CreateSoundShapingDialog_order on Order {
      id

      cart {
        id
        options
        product {
          id
          displayMode
          defaultOptions
        }
      }
    }
  `,
  cartItem: gql`
    fragment OrderSidebar_SoundShaping_CreateSoundShapingDialog_cartItem on CartItem {
      id
      options
      product {
        id
        displayMode
        defaultOptions
      }
    }
  `,
};
